import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { reveal } from "@/lib/motion";
import MaskLines from "@/components/MaskLines";
import { groupMonths, monthLabel, firstDow, DAGKORT } from "@/lib/calendar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TITLE_BY_STATUS = {
  available: "Beschikbaar",
  partial: "Nog beperkt beschikbaar",
  booked: "Volgeboekt",
  closed: "Gesloten",
};

export default function Agenda({ onPickDate }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [offset, setOffset] = useState(0);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    let live = true;
    fetch(`${BACKEND}/api/agenda?months=3`)
      .then((r) => r.json())
      .then((d) => {
        if (live) setData(d);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, []);

  const months = useMemo(() => (data ? groupMonths(data.days) : []), [data]);
  const monthKey = months[offset]?.[0];
  const days = months[offset]?.[1] ?? [];
  const blanks = monthKey ? firstDow(monthKey) : 0;

  const pick = (day) => {
    if (day.past || day.status === "booked" || day.status === "closed") return;
    setPicked(day.date);
    onPickDate?.(day.date);
  };

  return (
    <section id="agenda" className="section agenda" data-testid="agenda-section">
      <MaskLines lines={["Wanneer kunnen we", "aan de slag?"]} testId="agenda-titel" />
      <motion.p className="lead" {...reveal(0.08)}>
        Wij werken zeven dagen per week. Een dag is vol zodra alle plekken geboekt zijn.
      </motion.p>
      {failed ? (
        <p className="agenda-note" data-testid="agenda-error">
          De agenda kon niet geladen worden. Probeer het later opnieuw.
        </p>
      ) : null}
      {monthKey ? (
        <motion.div {...reveal(0.1, 12)}>
          <div className="agenda-head">
            <div className="agenda-month" data-testid="agenda-month-label">{monthLabel(monthKey)}</div>
            <div className="agenda-nav">
              <button
                type="button"
                aria-label="Vorige maand"
                disabled={offset === 0}
                onClick={() => setOffset((o) => Math.max(0, o - 1))}
                data-testid="agenda-prev"
              >
                ‹
              </button>
              <button
                type="button"
                aria-label="Volgende maand"
                disabled={offset === months.length - 1}
                onClick={() => setOffset((o) => Math.min(months.length - 1, o + 1))}
                data-testid="agenda-next"
              >
                ›
              </button>
            </div>
          </div>
          <div className="agenda-grid" data-testid="agenda-grid">
            {DAGKORT.map((d) => (
              <div className="agenda-dow" key={d}>{d}</div>
            ))}
            {Array.from({ length: blanks }).map((_, i) => (
              <div className="agenda-day is-empty" key={`b-${i}`} aria-hidden="true" />
            ))}
            {days.map((day) => {
              const disabled = day.past || day.status === "booked" || day.status === "closed";
              return (
                <button
                  type="button"
                  key={day.date}
                  title={day.past ? undefined : TITLE_BY_STATUS[day.status]}
                  disabled={disabled}
                  onClick={() => pick(day)}
                  className={[
                    "agenda-day",
                    day.past ? "is-past" : `is-${day.status}`,
                    picked === day.date ? "is-picked" : "",
                  ].join(" ").trim()}
                  data-testid={`agenda-day-${day.date}`}
                >
                  {Number(day.date.slice(8))}
                  {!day.past && day.status === "partial" ? (
                    <em className="agenda-day-tag">{day.spotsLeft} vrij</em>
                  ) : null}
                </button>
              );
            })}
          </div>
          <div className="agenda-legend" data-testid="agenda-legend">
            <span><i className="dot-available" /> Beschikbaar</span>
            <span><i className="dot-partial" /> Beperkt beschikbaar</span>
            <span><i className="dot-booked" /> Volgeboekt</span>
            <span><i className="dot-closed" /> Gesloten</span>
          </div>
          <p className="agenda-note">
            Klik op een vrije dag om je voorkeursdatum mee te sturen met je aanvraag.
          </p>
        </motion.div>
      ) : null}
    </section>
  );
}
